import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  Heading,
  Button,
  Flex,
  StackDivider,
  HStack,
  Spinner,
  Text,
  useDisclosure,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Switch
} from '@chakra-ui/react';
import {
  SETTLEMENT_SERVICE_DASHBOARD_STRINGS,
  TAB_STATES
} from '@views/SettlementServiceDashboard/constant';
import { useDispatch, useSelector } from 'react-redux';
import { MdAdd, MdEdit, MdOfflineBolt, MdOutlineCancel } from 'react-icons/md';
import SearchBox from '@components/SearchBox/SearchBox';
import Pagination from '@components/Pagination';
import {
  updateSettlementServiceAccountsReqData,
} from '@store/actions/reqDataActions';
import useDidMountEffect from '@hooks/useDidMount';
import { getAllAccounts } from '@store/actions/settlementServiceDashboardActions';
import useDebounce from '@hooks/useDebounce';
import ModalLayout from '@components/Modal/ModalLayout';
import ConfirmModal from '@components/Modal/ConfirmModal';
import DynamicFormField from '@components/DynamicFormField/DynamicFormField';
import useAuthRole from '@hooks/useAuthRole';
import { ROLES } from '@constants';
import { accountSettlementsConfig as config } from '@views/SettlementServiceDashboard/utils';
import { FaDotCircle } from 'react-icons/fa';
import { IoMdCheckmark } from 'react-icons/io';
import DynamicInputModal from '@components/DynamicInputModal';
import CopyableText from '@components/CopyableText';


const AccountSettlements = ({ selectedAccount, setSelectedAccount }) => {
  const { hasAnyAuthority } = useAuthRole();
  const dispatch = useDispatch();

  const reqData = useSelector(
    (state) => state.reqData.settlementServiceDashboard.accounts
  );
  const { totalPages, pageNumber, filters, searches } = reqData;
  
  const {
    data: accountSettlements,
    isLoading,
    error
  } = useSelector((state) => state.settlementServiceDashboard.accounts);

  // Local State Management
  const [accountSettlementToEdit, setAccountSettlementToEdit] = useState(null);
  const [formData, setFormData] = useState({});
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [filterParams, setFilterParams] = useState(filters || {});
  const [searchParams, setSearchParams] = useState({});
  
  const debouncedFilterParams = useDebounce(filterParams, 500);
  const debouncedSearchParams = useDebounce(searchParams, 500);

  const {
    isOpen: isAddNewAccountSettlementModalOpen,
    onClose: onAddNewAccountSettlementModalClose,
    onOpen: onAddNewAccountSettlementModalOpen
  } = useDisclosure();

  const {
    isOpen: isConfirmUpdateModalOpen,
    onOpen: onConfirmUpdateModalOpen,
    onClose: onConfirmUpdateModalClose
  } = useDisclosure();

  const STRING_CONSTANTS = SETTLEMENT_SERVICE_DASHBOARD_STRINGS.STRING_CONSTANTS[TAB_STATES.ACCOUNT];

  const fetchAccountSettlements = useCallback(() => {
    dispatch(
      getAllAccounts({
        payload: reqData
      })
    );
  }, [dispatch, reqData]);

  useDidMountEffect(() => {
    fetchAccountSettlements();
  }, [
    debouncedFilterParams,
    debouncedSearchParams,
    pageNumber
  ]);

  useEffect(() => {
    if (accountSettlementToEdit) {
      setFormData(
        config.entityToFormData({ entity: [accountSettlementToEdit] })
      );
    }
  }, [accountSettlementToEdit]);

  // Handle Page Change
  const handlePageChange = useCallback((page) => {
      const newPageNumber = Math.max(0, page - 1);
      dispatch(
        updateSettlementServiceAccountsReqData({
          ...reqData,
          pageNumber: newPageNumber
        })
      );
    },
    [dispatch, reqData]
  );

  const handleSearchChange = (search) => {
    setSearchParams((prev) => ({
      ...prev,
      searchKey: search.searchKey,
      searchValue: search.searchValue,
    }));
    dispatch(
      updateSettlementServiceAccountsReqData({
        ...reqData,
        searchKey: search.searchKey,
        searchValue: search.searchValue,
        pageNumber: 0,
      })
    );
  };

  const handleEditMode = useCallback((e, account) => {
    e.preventDefault();
    setAccountSettlementToEdit(account);
  }, []);

  const handleEditCleanUp = useCallback((e) => {
    e?.preventDefault();
    setFormData({});
    setAccountSettlementToEdit(null);
    onConfirmUpdateModalClose();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'number'
          ? Number(value)
          : value
    }));
  };

  const handleSaveUpdateAccountSettlement = useCallback(() => {
    if (accountSettlementToEdit) {
      setIsModalLoading(true);
      dispatch(
        config.updateAccountSettlement({
          accountId: accountSettlementToEdit?.id,
          payload: config.updateAccountSettlementPayloadConvertor({ formData }),
          onComplete: () => {
            setIsModalLoading(false);
            handleEditCleanUp(null);
            fetchAccountSettlements();
          }
        })
      );
    }
  }, [accountSettlementToEdit, formData, fetchAccountSettlements]);

  const handleAddNewAccountSettlementSubmit = (formData) => {
    setIsModalLoading(true);
    dispatch(
      config.addNewAccountSettlement({
        payload: config.addAccountSettlementPayloadConvertor({ formData }),
        onSuccess: () => {
          fetchAccountSettlements();
        },
        onComplete: () => {
          setIsModalLoading(false);
        }
      })
    );
    onAddNewAccountSettlementModalClose();
  };

  const getRowStyles = useCallback(
    (accountSettlement) => ({
      boxShadow:
        accountSettlementToEdit?.id === accountSettlement?.id
          ? '0px 10px 30px rgba(0, 0, 0, 0.6)'
          : 'none',
      backgroundColor:
        accountSettlementToEdit?.id === accountSettlement?.id
          ? 'black'
          : '#1c1c1c',
      border:
        accountSettlementToEdit?.id === accountSettlement?.id
          ? '6px solid #1c1c1c'
          : '3px solid black',
      opacity:
        accountSettlementToEdit &&
        accountSettlementToEdit?.id !== accountSettlement?.id
          ? 0.6
          : 1,
      transition: 'opacity 0.3s ease-in-out',
      filter:
        accountSettlementToEdit &&
        accountSettlementToEdit?.id !== accountSettlement?.id
          ? 'blur(1px)'
          : 'none',
      _hover: {
        backgroundColor:
          accountSettlementToEdit?.id === accountSettlement?.id
            ? 'black'
            : '#111111'
      }
    }),
    [accountSettlementToEdit]
  );

  // Render Table View
  const renderTableView = () => (
    <Box position={'relative'}>
      <Heading fontSize={'lg'} color={'colorPrimary'}>
        Account Settlements
      </Heading>
      <TableContainer
        border={'2px solid'}
        borderColor={'whiteAlpha.500'}
        borderRadius={'xl'}
        my={4}
      >
        <Table variant={'unstyled'}>
          <Thead>
            <Tr
              bg={'backGround'}
              borderBottom="2px solid"
              borderColor="whiteAlpha.500"
            >
              {accountSettlements &&
                accountSettlements.length > 0 &&
                accountSettlements[0]?.data.map((field, index) => (
                  <Th
                    whiteSpace={'nowrap'}
                    key={`${field.name}-${index}`}
                    style={field?.position?.style || {}}
                    textAlign={'center'}
                  >
                    <VStack fontSize="xs">
                      <Text>{field?.title}</Text>
                    </VStack>
                  </Th>
                ))}
            </Tr>
          </Thead>
          <Tbody>
            {accountSettlements.map((accountSettlement, index) => (
              <Tr
                key={accountSettlement?.id || index}
                {...getRowStyles(accountSettlement)}
              >
                {accountSettlement?.data?.map((field, index) => (
                  <Td
                    key={`${accountSettlement?.id}-${index}`}
                    whiteSpace={'nowrap'}
                    style={field?.position ? field?.position?.style : {}}
                  >
                    <HStack
                      justify={
                        field.showEditIcon ? 'space-between' : 'space-evenly'
                      }
                      key={`${accountSettlement?.id}-${index}`}
                      p={0}
                      transition="all 0.3s ease-in-out"
                      transform={
                        accountSettlementToEdit?.id === accountSettlement?.id
                          ? 'scale(0.9)'
                          : 'scale(1)'
                      }
                    >
                      {accountSettlementToEdit?.id === accountSettlement?.id &&
                      field.isEditable ? (
                        <DynamicFormField
                          key={`${index}-${field.value}`}
                          fieldConfig={config.toDynamicFormField(field)}
                          data={formData}
                          handleChange={handleChange}
                        />
                      ) : field.type === 'switch' ? (
                        <Switch
                          isDisabled
                          size="sm"
                          defaultChecked={field.value}
                          colorScheme="brand"
                          color="white"
                        />
                      ) : field.type === 'icon' ? (
                        <Box color={field.color}>
                          {field.value ? (
                            <MdOfflineBolt size="1.2rem" />
                          ) : (
                            <FaDotCircle size="1.2rem" />
                          )}
                        </Box>
                      ) : field.isCopyable ? (
                        <CopyableText
                          title={`${field?.title}`}
                          fontSize="xs"
                          color="white"
                        >
                          {field?.value || '-'}
                        </CopyableText>
                      ) : (
                        <Text fontSize="xs">
                          {field?.maskValue || field?.value || '-'}
                        </Text>
                      )}
                      {field.showEditIcon &&
                        hasAnyAuthority(ROLES.ADMIN_ROLE) &&
                        (accountSettlementToEdit?.id ===
                        accountSettlement?.id ? (
                          <HStack
                            justify={'space-between'}
                            key={`${accountSettlement?.id}-${index}`}
                            p={0}
                          >
                            <Button
                              colorScheme={'red'}
                              size="xs"
                              bg="black"
                              border={'2px solid'}
                              borderColor={'whiteAlpha.500'}
                              borderRadius={'full'}
                              onClick={(e) => handleEditCleanUp(e)}
                              leftIcon={<MdOutlineCancel size={'1rem'} />}
                              p={3}
                            >
                              Discard
                            </Button>
                            <Button
                              colorScheme={'brand'}
                              size="xs"
                              bg="black"
                              border={'2px solid'}
                              borderColor={'whiteAlpha.500'}
                              borderRadius={'full'}
                              onClick={onConfirmUpdateModalOpen}
                              leftIcon={<IoMdCheckmark size={'1rem'} />}
                              p={3}
                            >
                              Update
                            </Button>
                          </HStack>
                        ) : (
                          <Button
                            colorScheme={'brand'}
                            size="xs"
                            bg="black"
                            border={'1px solid'}
                            borderColor={'whiteAlpha.300'}
                            borderRadius={'lg'}
                            p={0}
                            onClick={(e) =>
                              handleEditMode(e, accountSettlement)
                            }
                          >
                            <MdEdit />
                          </Button>
                        ))}
                    </HStack>
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box h="100%" overflow="hidden">
      <ModalLayout
        isOpen={isConfirmUpdateModalOpen}
        onClose={onConfirmUpdateModalClose}
        textAlign="center"
        size={'md'}
      >
        <ConfirmModal
          handleSubmit={handleSaveUpdateAccountSettlement}
          prompt={`Are you sure you want to update the Account Settlement ${accountSettlementToEdit?.id}?`}
          isLoading={isModalLoading}
        />
      </ModalLayout>

      <ModalLayout
        isOpen={isAddNewAccountSettlementModalOpen}
        onClose={onAddNewAccountSettlementModalClose}
      >
        <DynamicInputModal
          title={STRING_CONSTANTS.INPUT_MODAL_TITLE}
          isOpen={isAddNewAccountSettlementModalOpen}
          onClose={onAddNewAccountSettlementModalClose}
          onSubmit={handleAddNewAccountSettlementSubmit}
          fields={config.newConfigFieldsConfig() || []}
          isLoading={isModalLoading}
        />
      </ModalLayout>
      <VStack
        h="100%"
        overflowY="auto"
        overflowX="hidden"
        alignItems="stretch"
        gap={4}
        px={4}
        pb={4}
        divider={<StackDivider borderColor="whiteAlpha.300" />}
      >
        <HStack justify={'space-between'}>
          <Heading size={'md'}> {STRING_CONSTANTS.HEADING} </Heading>
          <HStack spacing={2}>
            <Button
              colorScheme={'brand'}
              size="sm"
              bg="backGround"
              border={'2px solid'}
              borderColor={'whiteAlpha.300'}
              borderRadius={'lg'}
              p={4}
              leftIcon={<MdAdd size={'1.5rem'} />}
              onClick={onAddNewAccountSettlementModalOpen}
            >
              {STRING_CONSTANTS.ADD_NEW_ACCOUNT}
            </Button>
          </HStack>
        </HStack>

        {/*Search*/}
        <HStack justify={'flex-start'}>
          {!!searches?.length && (
            <SearchBox
              searches={searches}
              handleSearchChange={handleSearchChange}
            />
          )}
        </HStack>

        {/*Pagination*/}
        {totalPages > 1 && (
          <Pagination
            onPageChange={handlePageChange}
            currentPage={pageNumber + 1}
            totalPages={totalPages}
          />
        )}

        {/*Data Display*/}
        {isLoading ? (
          <Flex justifyContent="center" alignItems="center" w="full" h="full">
            <Spinner thickness="4px" size="lg" color="colorPrimary" />
          </Flex>
        ) : !!error ? (
          <Box
            p={4}
            borderRadius="xl"
            bg={'backGround'}
            borderWidth="1px"
            borderColor={'whiteAlpha.300'}
          >
            <Text color="red.500" fontSize="md">
              Error Fetching Accounts Settlements: {error}
            </Text>
          </Box>
        ) : accountSettlements?.length ? (
          renderTableView()
        ) : (
          <Box
            p={4}
            borderRadius="xl"
            bg={'backGround'}
            borderWidth="1px"
            borderColor={'whiteAlpha.300'}
          >
            <Text color="white" fontSize="md">
              No Account Settlement Found!
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default AccountSettlements;
